/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { ChangeEvent, Component, FormEvent } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { AppState } from '../../app.state';
import Link from '../../_shared/link';
import FormTitle from '../../_shared/form-title';
import { get, set } from 'lodash';
import styles from './edit-profile.styles';
import { message } from 'antd';
import {
  buildErrorMsgFromForm,
  uppercaseWords,
  isPB,
  stopProp,
} from '../../utils';
import { Button } from '../../_shared/button';
import { User } from '../../_shared/interfaces/user';
import { updateProfile } from '../../_shared/services/manage-profiles.service';
import { Profile, _Profile } from '../../_shared/interfaces/profile';
import { WithForm } from '../../_shared/form';

interface IProps extends RouteComponentProps {
  sState: AppState;
  updateProfiles: () => Promise<void>;
  form?: any;
}

class EditProfile extends Component<IProps> {
  state = {
    back: undefined,
    profile: undefined as Profile | undefined,
    loading: false,
    error: null,
    success: null,
    nameError: null,
    toDisplay: [
      {
        title: 'Profile Name',
        var: 'name',
        transform: (value: string) => uppercaseWords(value),
        options: {
          initialValue: get(this.props, 'history.location.state.profile.name'),
          rules: [
            {
              required: true,
              message: 'Name is required',
            },
            {
              max: 16,
              message: 'Name is limited to 16 characters. ',
            },
          ],
        },
      },
    ],
  };

  componentDidMount = async () => {
    const {
      history: { location },
    } = this.props;
    const back = get(location, 'state.back');
    const profile = get(location, 'state.profile', new _Profile());
    const profileOriginal = get(location, 'state.profile');
    this.setState({
      back,
      profile,
      profileOriginal,
    });
  };

  submit = async (e: MouseEvent | FormEvent) => {
    stopProp(e);
    if (this.state.loading) {
      return;
    }

    this.state.loading = true;
    this.state.error = null;
    this.state.success = null;
    this.state.nameError = null;
    this.setState(this.state);
    const { profile } = this.state;

    this.props.form.validateFieldsAndScroll(async (err: any, values: any) => {
      if (err) {
        const error = buildErrorMsgFromForm(err);
        return this.setState({ error, loading: false });
      }

      try {
        const _profile = new _Profile({
          ...((profile as Profile) || {}),
        } as any) as Profile;

        const date = new Date().toISOString();
        const currentUser = get(this.props.sState, 'auth.user', {}) as User;

        set(_profile, 'updatedAt', date);
        set(_profile, 'updatedBy', currentUser.userId);

        // AER-1692: this screen doubles as the create path (componentDidMount
        // falls back to `new _Profile()` when navigated without state), but it
        // never stamped createdBy. `_Profile`'s `createdBy = ''` default is not
        // protection — removeEmpty() deletes it before the request is sent, and
        // nothing server-side or in the axios interceptor adds it back. Guard on
        // the same "arrived without a profile" signal componentDidMount uses so
        // an edit never re-attributes an existing profile to whoever saved it.
        const isAdd = !get(this.props, 'history.location.state.profile');
        if (isAdd && !get(_profile, 'createdBy')) {
          set(_profile, 'createdAt', get(_profile, 'createdAt') || date);
          set(_profile, 'createdBy', currentUser.userId);
        }

        set(_profile, 'name', values.name);
        await updateProfile(_profile);
        this.props.updateProfiles();
        message.success('Profile has been saved');
        this.setState({
          loading: false,
        });
        if (get(this.props, 'location.pathname').includes('editProfileAdmin')) {
          return this.goToMyOrg();
        }
        this.manageProfiles(_profile);
      } catch (error) {
        message.error('Profile could not be updated');
        return this.setState({
          loading: false,
        });
      }
    });
  };

  setFieldsOriginal = () => {
    const { toDisplay, profile } = this.state;
    const {
      form: { setFieldsValue },
    } = this.props;

    const Profile = new _Profile({
      ...(profile || {}),
    } as Profile);
    toDisplay.map(item => {
      item.var && setFieldsValue({ [item.var]: Profile[item.var] });
    });
  };

  goToMyOrg = () => this.props.history.push('/dashboard/myOrg');

  goBack = () => {
    this.setFieldsOriginal();
    this.props.history.goBack();
  };

  manageProfiles = (profile: Profile) => {
    this.props.history.push('/dashboard/profiles', {
      profile,
    });
  };
  onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ nameError: null });
    const name = event.target.value;

    if (name.length > 16) {
      this.setState({
        nameError: 'Preset names are limited to 16 characters.',
      });
    }
  };

  render() {
    const { back, profile, toDisplay, loading, error, nameError } = this.state;
    const { sState, form: { Form } } = this.props;
    const ispb = isPB(sState);
    const disabled = !!error || !!nameError;
    return (
      <div css={css(styles.container)}>
        {!!back && (
          <Link
            css={css`
              margin-bottom: 5px;
            `}
            onClick={this.goBack}
          >
            {back}
          </Link>
        )}
        <div
          css={css(
            styles.container,
            !ispb && `flex-direction: row;`,
            ispb && 'margin-bottom: 0px;'
          )}
        >
          {profile ? (
            <div css={css(styles.mainContainer)}>
              <FormTitle size={'20px'} title={'Edit Handpiece Profile'} />

              <Form
                layout="vertical"
                onSubmit={this.submit}
                items={toDisplay}
              >
                <Button
                  title={'Cancel'}
                  disabled={loading}
                  css={css`
                    margin-right: 10px;
                  `}
                  outline={true}
                  onClick={this.goBack}
                />
                <Button
                  title={'Save'}
                  loading={loading}
                  onClick={this.submit}
                  disabled={disabled}
                />
              </Form>
            </div>
          ) : (
            <div>No Preset Profile Provided</div>
          )}
        </div>
      </div>
    );
  }
}

export const EditProfileComponent = WithForm(withRouter(EditProfile));
